import { ComponentType } from 'react';

/**
 * Registry centralizado de rutas → componentes para sistema de Tabs
 * 
 * UBICACIÓN: app/routes/ (puede importar de modules/ sin violar arquitectura)
 * RESPONSABILIDAD: Mapeo de rutas a componentes para renderizado en tabs
 * 
 * IMPORTANTE: Mantener sincronizado al añadir nuevos módulos
 */

// ============================================================================
// IMPORTS DE MÓDULOS
// ============================================================================

// Administración
import { RolesPage } from '@/modules/administracion/roles';
import { TiposUsuarioPage } from '@/modules/administracion/tipos-usuario';

// UI Showcase - Design System Documentation
import { ButtonsPage } from '@/docs/ui-showcase/buttons/pages/ButtonsPage';
import { FormsPage } from '@/docs/ui-showcase/forms/pages/FormsPage';
import { DataTablesPage } from '@/docs/ui-showcase/datatables/pages/DataTablesPage';
import { ModalsPage } from '@/docs/ui-showcase/modals/pages/ModalsPage';
import { AlertsPage } from '@/docs/ui-showcase/alerts/pages/AlertsPage';
import { ToastsPage } from '@/docs/ui-showcase/toasts/pages/ToastsPage';
import { IconsPage } from '@/docs/ui-showcase/icons/pages/IconsPage';
import { ColorsPage } from '@/docs/ui-showcase/colors/pages/ColorsPage';
import { TypographyPage } from '@/docs/ui-showcase/typography/pages/TypographyPage';
import { CardsPage } from '@/docs/ui-showcase/cards/pages/CardsPage';
import { TabsPage } from '@/docs/ui-showcase/tabs/pages/TabsPage';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Tipo para componentes
 */
type PageComponent = ComponentType<any>;

/**
 * Dashboard - Componente simple (será reemplazado por página real)
 */
const DashboardPage: ComponentType = () => {
    return <div>Dashboard - En construcción</div>;
};

// Placeholders para módulos sin página implementada
const UsuariosPage: ComponentType = () => <div>Módulo de Usuarios - En desarrollo</div>;
const ItemsPage: ComponentType = () => <div>Módulo de Items - En desarrollo</div>;
const AccesosPage: ComponentType = () => <div>Módulo de Accesos - En desarrollo</div>;
const ParametrosPage: ComponentType = () => <div>Módulo de Parámetros - En desarrollo</div>;

// ============================================================================
// REGISTRY
// ============================================================================

/**
 * Mapa de rutas a componentes
 * 
 * Para añadir una nueva ruta:
 * 1. Importar el componente de páginas arriba (si existe)
 * 2. O crear un placeholder temporal
 * 3. Añadir entrada aquí con la ruta exacta como key
 * 4. La ruta debe coincidir con la URL del menú
 */
export const TAB_ROUTE_REGISTRY: Record<string, PageComponent> = {
    // Dashboard
    '/dashboard': DashboardPage,

    // ══════════════════════════════════════════════════════════════
    // ADMINISTRACIÓN
    // ══════════════════════════════════════════════════════════════
    '/administracion/roles': RolesPage,
    '/administracion/tipos-usuario': TiposUsuarioPage,
    '/administracion/usuarios': UsuariosPage,           // Placeholder
    '/administracion/items': ItemsPage,                 // Placeholder
    '/administracion/accesos': AccesosPage,             // Placeholder
    '/administracion/parametros': ParametrosPage,       // Placeholder

    // ══════════════════════════════════════════════════════════════
    // DEVELOP KIT - UI Components Demo
    // ══════════════════════════════════════════════════════════════
    '/develop-kit/buttons': ButtonsPage,
    '/develop-kit/forms': FormsPage,
    '/develop-kit/datatables': DataTablesPage,
    '/develop-kit/modals': ModalsPage,
    '/develop-kit/alerts': AlertsPage,
    '/develop-kit/toasts': ToastsPage,
    '/develop-kit/icons': IconsPage,
    '/develop-kit/colors': ColorsPage,
    '/develop-kit/typography': TypographyPage,
    '/develop-kit/cards': CardsPage,
    '/develop-kit/tabs': TabsPage,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Obtiene el componente para una ruta específica
 * 
 * @param route - Ruta completa (ej: '/administracion/roles')
 * @returns Componente para renderizar o null si no existe
 */
export const getComponentForRoute = (route: string): PageComponent | null => {
    return TAB_ROUTE_REGISTRY[route] || null;
};

/**
 * Verifica si una ruta tiene un componente mapeado
 * 
 * @param route - Ruta a verificar
 * @returns true si existe componente para la ruta
 */
export const hasComponentForRoute = (route: string): boolean => {
    return route in TAB_ROUTE_REGISTRY;
};

/**
 * Obtiene todas las rutas registradas
 * 
 * @returns Array de rutas disponibles
 */
export const getRegisteredRoutes = (): string[] => {
    return Object.keys(TAB_ROUTE_REGISTRY);
};
